// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHeaderUpdate } from "@sc/App/Handlers/XNG/Header";
import useSize, { useElementSize } from "@sc/App/Hooks/useSize";
import { CustomAppState } from "@sc/App/Store/Reducers";
import {
    getCartLinkWidget,
    getEmptyCartAlertWidget,
    getHeaderSearchWidget,
    getHeaderSignInWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
    xIsAuthenticated,
} from "@sc/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@sc/XDL/constants";
import Close from "@sc/XDL/Icons/Close";
import Menu from "@sc/XDL/Icons/Menu";
import { color, layoutContainer, parseBoolean, xClassNames, xScreen } from "@sc/XDL/Utilities";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        headerSearchWidget: getHeaderSearchWidget(state),
        isTabletScreen: state.xCommon.isTabletScreen,
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        // XNG-Change
        isHomepage: getCurrentPage(state).type === "HomePage",
        collectCustomerAddress: parseBoolean(getSession(state)?.properties?.collectCustomAddress || "false"),
        emptyCartAlertWidget: getEmptyCartAlertWidget(state),
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    navigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    // XNG-Change
    logoLinksSearchWrapper?: InjectableCss;
    rightSectionWrapper?: InjectableCss;
    itemsWrapper?: InjectableCss;
    mainNavigationPageWrapper?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    closeMenuClickable?: ClickableProps;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 50%;
            z-index: 1;
            opacity: 0;
            transform: translateY(-100%) translateX(-50%);
            padding: ${props => props.theme.header.padding};
            ${generateBorder}
            ${generateShadow};
        `,
    },
    logoWrapper: {
        css: css`
            align-self: center;
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        logo: {
            wrapper: {
                css: css`
                    width: 218px;
                    height: 40px;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                `,
            },
        },
    } as Styles);

const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

const InnerWrapper = styled.div<{ isEdit?: boolean; isCheckoutPage?: boolean } & InjectableCss>`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    ${(props: any) => {
        if (props.isEdit) {
            return "padding: 30px 10px;";
        }
        return "padding: 8px 16px;";
    }}
    width: 100%;
    justify-content: space-between;
    margin: 0 auto;
    ${({ theme, isEdit }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                            `
                          : null,
                  ),
              );
    }}
    & .logo-and-search-wrapper {
        display: flex;
        align-items: center;
        flex-grow: 1;
        gap: 59px;
    }
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    // <XNG-Changes>
    const [showMainNavigation, setShowMainNavigation] = useState(false);
    const headerWidth = typeof document !== "undefined" ? useElementSize(document.body)?.width : "100%";
    const { isTabletScreen, isAuthenticated } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
    }));
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef);
    // </XNG-Changes>

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            // XNG-Changes
            // const windowWidth = Math.round(window.innerWidth);
            let isSticky = false;
            if (scrollPos > Number(props.theme.header.triggerPoint)) {
                setIsShow(true);
                isSticky = true;
            } else if (isShow && scrollPos < Number(props.theme.header.triggerPoint) + 150) {
                setIsShow(false);
                isSticky = false;
            }

            props.xHeaderUpdate({ key: "isSticky", value: isSticky });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    // <XNG-Changes>
    useEffect(() => {
        if (headerHeight?.height) {
            props.xHeaderUpdate({ key: "stickyHeader", value: `${headerHeight.height}` });
        }
    }, [headerHeight?.height]);

    const openNavigationDrawer = () => {
        setShowMainNavigation(prevState => !prevState);
    };

    if (isTabletScreen || !isAuthenticated) {
        return null;
    }
    // </XNG-Changes>

    // XNG-Changes: structure
    return (
        <Transition in={isShow} timeout={0}>
            {state => (
                <Page
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    // XNG-Changes
                    data-test-selector="compact-header"
                    className={xClassNames.append(isShow ? "shown-compact-header" : "hidden-compact-header")}
                    style={{
                        transition: "all 200ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                        // XNG-Changes
                        maxWidth: headerWidth,
                    }}
                    {...{ showMainNavigation }}
                >
                    <StyledWrapper ref={headerRef}>
                        <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                            <InnerWrapper
                                {...styles.pageInnerWrapper}
                                isEdit={isEdit}
                                isCheckoutPage={props.isCheckoutPage}
                            >
                                <StyledWrapper className="logo-and-search-wrapper" {...styles.logoLinksSearchWrapper}>
                                    <StyledWrapper {...styles.logoWrapper}>
                                        {!props.isCheckoutPage && (
                                            <Clickable
                                                onClick={openNavigationDrawer}
                                                data-test-selector="expandMobileMenu"
                                                aria-label="main-navigation"
                                                {...styles.closeMenuClickable}
                                            >
                                                <Icon
                                                    src={showMainNavigation ? Close : Menu}
                                                    size={showMainNavigation ? 14 : 18}
                                                    color="text.link"
                                                />
                                            </Clickable>
                                        )}
                                        {createWidgetElement("Basic/Logo", {
                                            fields: {
                                                mobileSpecificImage: props.logoImageWidget?.logoImage,
                                                logoImage: props.logoImageWidget?.logoImage,
                                            },
                                        })}
                                    </StyledWrapper>

                                    {!props.isCheckoutPage && (
                                        <StyledWrapper {...styles.searchWrapper}>
                                            {createWidgetElement("Header/HeaderSearchInput", {
                                                fields: {
                                                    ...props.headerSearchWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                {!props.isCheckoutPage && (
                                    <StyledWrapper {...styles.rightSectionWrapper}>
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement("Common/HeaderSignIn", {
                                                fields: {
                                                    ...props.headerSignInWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.itemsWrapper}>
                                            {createWidgetElement("Common/CartLink", {
                                                fields: {
                                                    ...props.cartLinkWidget,
                                                },
                                            })}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </InnerWrapper>
                            {!props.isCheckoutPage ? (
                                <StyledWrapper {...{ showMainNavigation }} {...styles.navigationWrapper}>
                                    <Page {...styles.mainNavigationPageWrapper}>
                                        <StyledWrapper {...styles.mainNavigationWrapper}>
                                            {createWidgetElement("Header/MainNavigation", {
                                                fields: { ...props.mainNavigationWidget },
                                            })}
                                        </StyledWrapper>
                                    </Page>
                                </StyledWrapper>
                            ) : (
                                <></>
                            )}
                            {props.isHomepage && props.collectCustomerAddress ? (
                                <>
                                    {createWidgetElement("EmptyCartAlert/EmptyCartAlert", {
                                        fields: {
                                            ...props.emptyCartAlertWidget,
                                        },
                                    })}
                                </>
                            ) : (
                                <></>
                            )}
                        </StylesProvider>
                    </StyledWrapper>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
